import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createParent,
  deleteParent,
  getParentProfile,
  getParentById,
  parentResendOtp,
  parentVerifyOtp,
  updateParent,
  getParents,
  parentChangeEmail,
  parentSendEmailChangeOtp,
} from './parentApi';
import { toast } from 'react-toastify';

export const useGetParent = () => {
  return useQuery({
    queryKey: ['parent'],
    queryFn: getParentProfile,
    staleTime: 0,
    cacheTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

export const useGetParents = () => {
  return useQuery({
    queryKey: ['parents'],
    queryFn: getParents,
    onError: (error) => {
      console.error('Failed to fetch parents:', error);
      toast.error(
        'Failed to load parents: ' + (error.message || 'Unknown error')
      );
    },
  });
};

export const useGetParentById = (ParentId) => {
  return useQuery({
    queryKey: ['parents', ParentId],
    queryFn: () => getParentById(ParentId),
  });
};

export const useCreateParent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['create_parent'],
    mutationFn: createParent,
    onSuccess: () => {
      queryClient.invalidateQueries(['parents']);
      toast.success('Parent profile created successfully');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useUpdateParent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ([id, values]) => updateParent(id, values),
    onSuccess: () => {
      // Invalidate both parents and children queries since parent data might be shown in child views
      toast.success('Parent profile updated successfully');
      queryClient.invalidateQueries(['parents']);
      queryClient.invalidateQueries(['children']);
    },
  });
};

export const useDeleteParent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete_parent'],
    mutationFn: deleteParent,
    onSuccess: () => {
      queryClient.invalidateQueries(['parents']);
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useVerifyOtp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['parent_verify_otp'],
    mutationFn: parentVerifyOtp,
    onSuccess: () => {
      queryClient.invalidateQueries(['parent']);
      toast.success('Email verified successfully!');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useResendOtp = () => {
  return useMutation({
    mutationKey: ['parent_resend_otp'],
    mutationFn: parentResendOtp,
    onSuccess: () => {
      toast.success('Verification code sent to your email');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useChangeEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['parent_change_email'],
    mutationFn: parentChangeEmail,
    onSuccess: () => {
      queryClient.invalidateQueries(['parent']);
      toast.success('Email changed successfully!');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};

export const useSendChangeEmailOtp = () => {
  return useMutation({
    mutationKey: ['parent_send_change_email_otp'],
    mutationFn: parentSendEmailChangeOtp,
    onSuccess: () => {
      toast.success('Verification code sent to your new email');
    },
    onError: (error) => {
      console.log(error.response.data.message);
      toast.error(error.response.data.message);
    },
  });
};
